import type { LatLng } from '@mobi/shared';

export interface MapMarker {
  point: LatLng;
  kind: 'origin' | 'destination' | 'driver' | 'passenger';
  label?: string;
  /** desenha carro ou moto no lugar do círculo; só vale para `driver` */
  vehicleType?: 'car' | 'moto';
  /** graus a partir do norte; gira o veículo para onde ele aponta */
  heading?: number;
  /**
   * Identidade estável entre atualizações. Sem ela, dois motoristas na
   * tela ao mesmo tempo (a lista de "perto de você") dividiriam a mesma
   * referência de rumo, e o cálculo de para onde cada um aponta se
   * misturaria com a posição do outro.
   */
  id?: string | number;
  /** rota autenticada da foto do outro lado da corrida; sem ela, iniciais */
  photoUrl?: string | null;
}

export interface MapBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

export interface RideMapProps {
  markers: MapMarker[];
  /** janela fixa; sem ela, enquadra os marcadores */
  bounds?: MapBounds;
  /** polyline codificada (precisão 5) devolvida pelo roteador */
  routePolyline?: string | null;
  /**
   * Trajeto a pé, desenhado pontilhado. Linha reta de propósito: o
   * roteador está configurado para carro, e uma rota de carro para 80 m
   * de calçada mandaria a pessoa dar a volta no quarteirão.
   */
  walkPath?: LatLng[] | null;
  /** habilita escolher um ponto tocando no mapa */
  onPick?: (point: LatLng) => void;
  className?: string;
  interactive?: boolean;
}

/**
 * Janela que enquadra exatamente os pontos dados, nada mais.
 *
 * Existe para não depender do ajuste automático por `markers`: naquele
 * caminho, qualquer marcador extra no mapa (um carro por perto, por
 * exemplo) entraria na conta e abriria o zoom toda vez que aparecesse ou
 * sumisse — mesmo sem ter nada a ver com o que a tela quer mostrar.
 */
export function boundsOfPoints(points: LatLng[]): MapBounds | null {
  if (points.length === 0) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  return { minLat, maxLat, minLng, maxLng };
}

/** Janela geográfica quadrada em torno de um ponto, com raio em metros. */
export function boundsAround(center: LatLng, radiusMeters: number): MapBounds {
  const dLat = radiusMeters / 111_320;
  const dLng = radiusMeters / (111_320 * Math.max(0.2, Math.cos((center.lat * Math.PI) / 180)));
  return {
    minLat: center.lat - dLat,
    maxLat: center.lat + dLat,
    minLng: center.lng - dLng,
    maxLng: center.lng + dLng,
  };
}

/**
 * Rumo de A para B, em graus a partir do norte.
 *
 * O `heading` do GPS do celular vem nulo com o carro parado e em boa
 * parte dos Android mesmo andando. Quando falta, o rumo entre duas
 * leituras seguidas diz para onde o carro aponta — que é o que faz o
 * ícone parecer seguir a rua em vez de deslizar de lado.
 */
export function bearingDegrees(from: LatLng, to: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  return (Math.atan2(y, x) * 180) / Math.PI;
}

/**
 * Decodifica polyline com precisão 5 — o formato que o OpenRouteService
 * devolve em `geometry`.
 */
export function decodePolyline(encoded: string): LatLng[] {
  const points: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    for (const axis of ['lat', 'lng'] as const) {
      let result = 0;
      let shift = 0;
      let byte: number;
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);

      const delta = result & 1 ? ~(result >> 1) : result >> 1;
      if (axis === 'lat') lat += delta;
      else lng += delta;
    }
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  return points;
}

const LEGEND_COLOR: Record<MapMarker['kind'], string> = {
  origin: 'bg-ink',
  destination: 'bg-brand',
  driver: 'bg-ink',
  passenger: 'bg-warn',
};

export function MapLegend({ items }: { items: { kind: MapMarker['kind']; label: string }[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-faint">
      {items.map((i) => (
        <span key={i.kind} className="inline-flex items-center gap-1.5">
          <span className={`inline-block h-2 w-2 rounded-full ${LEGEND_COLOR[i.kind]}`} />
          {i.label}
        </span>
      ))}
    </div>
  );
}
